import { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { ReactReduxContext } from 'react-redux';
import get from 'lodash/get';
import unsetInPlace from 'lodash/unset';
import isEqual from 'lodash/isEqual';

import setInPlace from 'lodash/set';
import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';
import SCOPE_SUBSCRIPTION_KEY from 'ia-react-core/~/constants/#SCOPE_SUBSCRIPTION_KEY';
import SUBSCRIPTIONS_KEY from 'ia-react-core/~/constants/#SUBSCRIPTIONS_KEY';
import { Subscription } from 'ia-react-core/~/interfaces/Subscription';
import keys from 'ia-react-core/~/utils/accessors/keys';
import { SubscriptionStore } from 'ia-react-core/~/interfaces/SubscriptionStore';
import ScopeContext from '../../components/redux/#ScopeContext';
import FormContext from '../../components/form/FormContext';
import getScoped from '../../utils/redux/getScoped';
import mapScopeToProps from '../../utils/redux/mapScopeToProps';
import stateChangesMap from '../../utils/redux/#stateChangesMap';

import getSubscriptionPaths from './getSubscriptionPaths';

const useSubscriptionToChanges = (keyMap: Record<string, string>, { scope: scopeOverride }: { scope?: string[] } = {}) => {
  const { store } = useContext(ReactReduxContext) as unknown as { store: AugmentedStore<unknown>};
  const { scope: orginalScope } = useContext(ScopeContext);
  const { formScope, ancestorValuesDst } = useContext(FormContext);

  const scope = useMemo(() => scopeOverride || orginalScope, [orginalScope, scopeOverride]);

  const subscriptionPaths = useMemo(() => getSubscriptionPaths(keyMap, scope), [scope, keyMap]);

  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const selector = mapScopeToProps(keyMap);
    return selector(store.getState(), { scope, formScope, ancestorValuesDst });
  });

  const [subscription] = useState(() => (key: string, path: string) => {
    const state = store.getState();
    const newProps = {};
    setInPlace(newProps, key, getScoped(state, path, []));

    setValues((prevProps) => ({ ...prevProps, ...newProps }));
  });

  const subscribe = useCallback(() => {
    const subscriptionStore = store[SCOPE_SUBSCRIPTION_KEY];

    subscriptionPaths.forEach(({ path, key }) => {
      const subscriptionScope = get(subscriptionStore, path);
      setInPlace(subscriptionStore, path, { [SUBSCRIPTIONS_KEY]: [], ...subscriptionScope });
      const subscriptionScopeList = get(subscriptionStore, `${path}.${SUBSCRIPTIONS_KEY}`) as Subscription[];

      subscriptionScopeList.push({
        subscription,
        path,
        key,
      });
    });
  }, [store, subscription, subscriptionPaths]);

  const unsubscribe = useCallback(() => {
    const subscriptionStore = store[SCOPE_SUBSCRIPTION_KEY];

    subscriptionPaths.forEach(({ path }) => {
      const subscriptionScopeList = get(subscriptionStore, `${path}.${SUBSCRIPTIONS_KEY}`) as Subscription[];

      if (subscriptionScopeList) {
        const subscriptionIndex = subscriptionScopeList.findIndex(({ subscription: currentSubcription }) => (
          subscription === currentSubcription
        ));
        subscriptionScopeList.splice(subscriptionIndex, 1);
      }

      const isSuuscriptionEmpty = isEqual(
        get(store[SCOPE_SUBSCRIPTION_KEY], path),
        { [SUBSCRIPTIONS_KEY]: [] },
      );

      if (isSuuscriptionEmpty) {
        unsetInPlace(store[SCOPE_SUBSCRIPTION_KEY], path);
      }
    });
  }, [store, subscription, subscriptionPaths]);

  const [invokeNestedSubscriptions] = useState(() => (subscriptionContext: SubscriptionStore, context: string, revisedChanges: Record<string, boolean>) => {
    if (subscriptionContext) {
      const subscriptionKeys = keys(subscriptionContext) as (string | typeof SUBSCRIPTIONS_KEY)[];
      subscriptionKeys.forEach((currentKey: string | typeof SUBSCRIPTIONS_KEY) => {
        const currentSubsriptionContext = subscriptionContext[currentKey];
        const subscriptionPath = `${context}.${currentKey}`;

        if (currentKey === SUBSCRIPTIONS_KEY) {
          if (currentSubsriptionContext && !revisedChanges[context]) {
            revisedChanges[context] = true;
            (currentSubsriptionContext as Subscription[]).forEach(({ key, path, subscription: sub }) => {
              sub(key, path);
            });
          }
        } else {
          invokeNestedSubscriptions(currentSubsriptionContext as SubscriptionStore, subscriptionPath, revisedChanges);
        }
      });
    }
  });

  const [onStateChange] = useState(() => () => {
    const subscriptionStore = store[SCOPE_SUBSCRIPTION_KEY];

    const state: object = store.getState();
    const changes = stateChangesMap.get(state);

    const revisedChanges: Record<string, boolean> = {};

    changes?.forEach((change) => {
      const changeParts = change.split('.');

      const subscriptionContext = get(subscriptionStore, change);
      invokeNestedSubscriptions(subscriptionContext as SubscriptionStore, change, revisedChanges);

      changeParts.reduce((remainingChangeParts) => {
        const currentPath = remainingChangeParts.join('.');
        const currentSubscriptions = get(subscriptionStore, `${currentPath}.${SUBSCRIPTIONS_KEY}`) as Subscription[];

        if (!revisedChanges[currentPath] && currentSubscriptions) {
          revisedChanges[currentPath] = true;
          currentSubscriptions.forEach(({ key, path, subscription: sub }) => {
            sub(key, path);
          });
        }

        return remainingChangeParts.slice(0, -1);
      }, changeParts.slice(0, -1));
    });
  });

  useEffect(() => {
    if (!store[SCOPE_SUBSCRIPTION_KEY]) {
      store[SCOPE_SUBSCRIPTION_KEY] = {};
      store.subscribe(onStateChange);
    }

    subscribe();

    setValues(
      mapScopeToProps(keyMap)(store.getState(), { scope, formScope, ancestorValuesDst }),
    );

    return unsubscribe;
  }, [onStateChange, keyMap, scope, store, subscribe, subscriptionPaths, unsubscribe, formScope, ancestorValuesDst]);

  return values;
};

export default useSubscriptionToChanges;
